import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// English translations
const enTranslations = {
  nav: {
    discover: 'Discover',
    howItWorks: 'How it works',
    blog: 'Blog',
    forOrganizers: 'For organizers',
    language: 'Language'
  },
  blog: {
    title: 'VentyVIP Blog',
    subtitle: 'Event guides, tips & insider knowledge',
    description: 'Expert advice for making the most of every event you attend. From festival packing lists to networking strategies — we\'ve got you covered.',
    readArticle: 'Read article',
    readTime: '{{time}} read',
    tags: 'Tags',
    shareArticle: 'Share Article',
    foundHelpful: 'Found this helpful?',
    sharePrompt: 'Share it with friends who are planning their next event.',
    moreArticles: 'More articles you\'ll love',
    backToHome: '← Back to home',
    featuredArticles: 'Featured Articles',
    latestArticles: 'Latest Articles',
    getTips: 'Get event tips in your inbox',
    newsletterDesc: 'Join 248,000+ subscribers getting our Thursday drop — five handpicked events plus expert tips for the weekend ahead.',
    subscribe: 'Subscribe',
    emailPlaceholder: 'you@email.com'
  },
  footer: {
    about: 'About',
    privacy: 'Privacy',
    terms: 'Terms',
    copyright: '© 2026 VentyVIP'
  },
  guides: {
    worldCup2026: 'World Cup 2026',
    philadelphiaSpotlight: 'Philadelphia Spotlight 🔔',
    phlBadge: '📍 PHL - FIFA',
    mainGuideBadge: '🏆 Main Guide',
    eventGuides: 'Event Guides'
  }
};

// Spanish translations
const esTranslations = {
  nav: {
    discover: 'Descubrir',
    howItWorks: 'Cómo funciona',
    blog: 'Blog',
    forOrganizers: 'Para organizadores',
    language: 'Idioma'
  },
  blog: {
    title: 'Blog de VentyVIP',
    subtitle: 'Guías, consejos y conocimiento exclusivo sobre eventos',
    description: 'Asesoramiento experto para aprovechar al máximo cada evento al que asistas. Desde listas de equipaje para festivales hasta estrategias de networking — te tenemos cubierto.',
    readArticle: 'Leer artículo',
    readTime: 'Lectura de {{time}}',
    tags: 'Etiquetas',
    shareArticle: 'Compartir artículo',
    foundHelpful: '¿Te resultó útil?',
    sharePrompt: 'Compártelo con amigos que estén planeando su próximo evento.',
    moreArticles: 'Más artículos que te encantarán',
    backToHome: '← Volver al inicio',
    featuredArticles: 'Artículos destacados',
    latestArticles: 'Últimos artículos',
    getTips: 'Recibe consejos sobre eventos en tu bandeja de entrada',
    newsletterDesc: 'Únete a más de 248,000 suscriptores que reciben nuestro envío del jueves — cinco eventos seleccionados más consejos expertos para el fin de semana.',
    subscribe: 'Suscribirse',
    emailPlaceholder: 'tu@email.com'
  },
  footer: {
    about: 'Acerca de',
    privacy: 'Privacidad',
    terms: 'Términos',
    copyright: '© 2026 VentyVIP'
  },
  guides: {
    worldCup2026: 'Copa Mundial 2026',
    philadelphiaSpotlight: 'Destacado de Filadelfia 🔔',
    phlBadge: '📍 PHL - FIFA',
    mainGuideBadge: '🏆 Guía Principal',
    eventGuides: 'Guías de Eventos'
  }
};

// French translations
const frTranslations = {
  nav: {
    discover: 'Découvrir',
    howItWorks: 'Comment ça marche',
    blog: 'Blog',
    forOrganizers: 'Pour organisateurs',
    language: 'Langue'
  },
  blog: {
    title: 'Blog VentyVIP',
    subtitle: 'Guides, conseils et savoir-faire événementiel',
    description: 'Conseils d\'experts pour profiter au maximum de chaque événement. Des listes de bagages pour festivals aux stratégies de networking — nous vous couvrons.',
    readArticle: 'Lire l\'article',
    readTime: 'Lecture {{time}}',
    tags: 'Tags',
    shareArticle: 'Partager l\'article',
    foundHelpful: 'Cela vous a aidé ?',
    sharePrompt: 'Partagez-le avec des amis qui planifient leur prochain événement.',
    moreArticles: 'Plus d\'articles que vous allez adorer',
    backToHome: '← Retour à l\'accueil',
    featuredArticles: 'Articles en vedette',
    latestArticles: 'Derniers articles',
    getTips: 'Recevez des conseils événementiels dans votre boîte mail',
    newsletterDesc: 'Rejoignez plus de 248 000 abonnés recevant notre envoi du jeudi — cinq événements sélectionnés plus des conseils d\'experts pour le week-end.',
    subscribe: 'S\'abonner',
    emailPlaceholder: 'vous@email.com'
  },
  footer: {
    about: 'À propos',
    privacy: 'Confidentialité',
    terms: 'Conditions',
    copyright: '© 2026 VentyVIP'
  },
  guides: {
    worldCup2026: 'Coupe du Monde 2026',
    philadelphiaSpotlight: 'Philadelphie en vedette 🔔',
    phlBadge: '📍 PHL - FIFA',
    mainGuideBadge: '🏆 Guide Principal',
    eventGuides: 'Guides d\'Événements'
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enTranslations },
      es: { translation: esTranslations },
      fr: { translation: frTranslations }
    },
    fallbackLng: 'en',
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    }
  });

export default i18n;

// Supported languages configuration
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' }
];

// Helper to get language prefix for URLs
export const getLangPrefix = (lng) => lng === 'en' ? '' : `/${lng}`;

// Helper to build URL with language
export const buildUrl = (path, lng) => {
  const prefix = getLangPrefix(lng);
  return `${prefix}${path}`;
};
